import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, ParamMap, Router } from '@angular/router';
import { Breadcrumb, BreadcrumbConfig, BreadcrumbDatum, BreadcrumbRoute, BreadcrumbRouteElement } from '@models/breadcrumb';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter, map, mergeMap, tap } from 'rxjs/operators';

/**
 *
 * BreadcrumbConfigs are built of BreadcrumbElement objects. There are 4 types: url, param, queryParam and queryParams.
 * queryParam takes a single string of a queryParam to be referenced. queryParams takes an array of queryParams
 * to be referenced. There is some error catching.
 *
 * The title of the breadcrumb can be set as a string or using a BreadcrumbElement.
 *


const ROUTES: Routes = [
  {
    path: 'account/:account/:timestamp',
    data: {
      browserTabTitle: 'Meter Scans',
      breadcrumbs: [
        {
          display: 'Dashboard',
          route: 'dashboard',
        },
        {
          display: ':username',
          route: 'meter-data/account/:account'
        },
        {
          display: ':timestamp',
          route: 'meter-data/account/:workOrder/:timestamp'
        }
      ]
    },
    component: MeterComponent
  }, {
  path: ':scanType',
  data: {
    browserTabTitle: 'Live Meter',
    breadcrumbs: [
      {
        display: 'Dashboard',
        route: 'dashboard',
      },
      {
        display: 'Live Meter Menu',
        route: 'live-view/?mac'
      },
      {
        display: ':scanType',
        route: 'live-view/?mac/:scanType'
      },
    ]
  },
  component: LiveComponent
}];


 */

@Injectable({
  providedIn: 'root'
})
export class BreadcrumbService {
  private _hideTitle = true;
  // @ts-ignore ALM-277061
  lastNavEnd = new BehaviorSubject<NavigationEnd>(null);
  breadcrumbs: Breadcrumb[] = [];

  get isTitleHidden() {
    return this._hideTitle;
  }

  get tabTitle() {
    return this.title.getTitle();
  }

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private title: Title,
  ) {
  }

  trackRouteChanges() {
    this.router.events.pipe(
      // @ts-ignore ALM-277061
      filter(event => event instanceof NavigationEnd),
      tap((navEnd: NavigationEnd) => this.lastNavEnd.next(navEnd)),
      map(() => this.activatedRoute),
      map(route => {
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route;
      }),
      filter(route => route.outlet === 'primary'),
      mergeMap((route: ActivatedRoute) => {
        return combineLatest([route.data, route.paramMap, route.queryParamMap]);
      })).subscribe((event: [any, ParamMap, ParamMap]) => {
        this.breadcrumbs = this.generateBreadcrumbs(event);
      });
  }

  generateBreadcrumbs(event: [any, ParamMap, ParamMap]): Breadcrumb[] {
    const browserTabTitle: string = event[0].browserTabTitle;
    const breadcrumbs: BreadcrumbDatum[] = event[0].breadcrumbs;

    this.title.setTitle(browserTabTitle);
    const breadcrumbConfig = this.parseBreadrumbData(breadcrumbs);
    return this.mapParams(breadcrumbConfig, event[1], event[2]);
  }

  mapParams(breadcrumbConfigs: BreadcrumbConfig[], params: ParamMap, queryParams: ParamMap): Breadcrumb[] {
    return breadcrumbConfigs.map((breadcrumbConfig: BreadcrumbConfig) => {
      const breadcrumb = new Breadcrumb();
      breadcrumb.breadcrumbRoute = this.generateRoute(breadcrumbConfig.routeElements as BreadcrumbRouteElement[], params, queryParams);
      if (breadcrumbConfig.routeElements) {
        breadcrumb.title = this.generateTitle(breadcrumbConfig.display, params, queryParams);
      } else if (breadcrumbConfig.display) {
        breadcrumb.title = breadcrumbConfig.display.value;
      }

      return breadcrumb;
    });
  }

  generateRoute(routeElements: BreadcrumbRouteElement[], params: ParamMap, queryParams: ParamMap): BreadcrumbRoute {
    return routeElements.reduce((breadcrumbRoute: BreadcrumbRoute, breadcrumbRouteElement: BreadcrumbRouteElement) => {
      switch (breadcrumbRouteElement.type) {
        case 'string':
          breadcrumbRoute.url += `/${breadcrumbRouteElement.value}`;
          break;
        case 'param':
          breadcrumbRoute.url += `/${params.get(breadcrumbRouteElement.value)}`;
          break;
        case 'queryParam':
          breadcrumbRoute.queryParams[breadcrumbRouteElement.value] = queryParams.get(breadcrumbRouteElement.value) as string;
          break;
      }
      return breadcrumbRoute;
    }, new BreadcrumbRoute());
  }

  generateTitle(titleElement: BreadcrumbRouteElement, params: ParamMap, queryParams: ParamMap): string {
    switch (titleElement.type) {
      case 'param':
        return this.toTitle(params.get(titleElement.value) as string);
      case 'queryParam':
        return queryParams.get(titleElement.value) as string;
      default:
        return titleElement.value;
    }
  }

  toTitle(text: string): string {
    const words = text.split(/[\/\-_]+/);
    return words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ').trim();
  }

  toggleTitle(showHide?: 'show' | 'hide') {
    switch (showHide) {
      case 'show':
        this._hideTitle = false;
        break;
      case 'hide':
        this._hideTitle = true;
        break;
      default:
        this._hideTitle = !this._hideTitle;
    }
  }

  parseBreadrumbData(breadcrumbData: BreadcrumbDatum[]): BreadcrumbConfig[] {
    return breadcrumbData.map(breadcrumbDatum => {
      if (breadcrumbDatum.route) {
        const routeElements = breadcrumbDatum.route.replace(/^\//g, '').split('/').map(routePart => this.parseRoutePart(routePart));

        return new BreadcrumbConfig(this.parseRoutePart(breadcrumbDatum.display), routeElements);
      } else {
        return new BreadcrumbConfig(this.parseRoutePart(breadcrumbDatum.display));
      }
    });
  }

  parseRoutePart(routePart: string): BreadcrumbRouteElement {
    switch (routePart.charAt(0)) {
      case ':':
        return new BreadcrumbRouteElement('param', routePart.replace(/^:/g, ''));
      case '?':
        return new BreadcrumbRouteElement('queryParam', routePart.replace(/^\?/g, ''));
      default:
        return new BreadcrumbRouteElement('string', routePart);
    }
  }

}
